import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import "./LeaderBoardPage.css";

export default function LeaderboardPage() {
  const { userId } = useParams();


  const activities = useQuery(
    api.progress.getTopProgressActivities,
    userId
      ? { userId }
      : "skip"
  );


  if (activities === undefined) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-container">
          <div className="leaderboard-loading">
            Loading top progress activities...
          </div>
        </div>
      </div>
    );
  }

  // Sort highest score first
  const sortedActivities = [...activities].sort(
    (a, b) => b.score - a.score
  );

  // Top 10 for chart
  const chartData = sortedActivities
    .slice(0, 10)
    .map((activity, index) => ({
      name: activity.activity,
      score: Number(activity.score ?? 0),
      rank: index + 1,
    }));

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">

        <div className="leaderboard-header">
          <p className="leaderboard-eyebrow">
            Progress Leaderboard
          </p>

          <h1 className="leaderboard-title">
            Top Progress Activities
          </h1>

          <p className="leaderboard-subtitle">
            Your highest-scoring progress activities.
          </p>
        </div>

        {/* Chart */}

        {chartData.length > 0 && (
          <section className="leaderboard-chart-card">

            <div className="leaderboard-section-heading">
              <p className="leaderboard-section-label">
                Performance
              </p>

              <h2 className="leaderboard-section-title">
                Top Activities
              </h2>
            </div>

            <div className="leaderboard-chart-container">
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 10,
                    bottom: 60,
                  }}
                >

                  <CartesianGrid
                    stroke="#26382d"
                    strokeDasharray="3 3"
                  />

                  <XAxis
                    dataKey="name"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    tick={{
                      fill: "#94a3a8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <YAxis
                    domain={[0, 100]}
                    tick={{
                      fill: "#94a3a8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={{
                      stroke: "#33443a",
                    }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0b120e",
                      border: "1px solid rgba(74, 222, 128, 0.22)",
                      borderRadius: "12px",
                      color: "#f4f7f4",
                    }}
                    labelStyle={{
                      color: "#86efac",
                    }}
                    itemStyle={{
                      color: "#f4f7f4",
                    }}
                  />

                  <Bar
                    dataKey="score"
                    name="Score"
                    fill="#22c55e"
                    radius={[7, 7, 0, 0]}
                    maxBarSize={46}
                  />

                </BarChart>
              </ResponsiveContainer>
            </div>

          </section>
        )}

        {/* Sorted Activities */}

        {sortedActivities.length > 0 && (
          <section className="leaderboard-rankings-card">

            <div className="leaderboard-rankings-header">
              <p className="leaderboard-section-label">
                Rankings
              </p>

              <h2 className="leaderboard-section-title">
                Activity Rankings
              </h2>
            </div>

            <div className="leaderboard-ranking-list">
              {sortedActivities.map(
                (activity, index) => (
                  <div
                    key={activity._id}
                    className="leaderboard-ranking-row"
                  >

                    <div className="leaderboard-ranking-left">

                      <div className="leaderboard-rank">
                        #{index + 1}
                      </div>

                      <div className="leaderboard-activity">
                        <p className="leaderboard-activity-name">
                          {activity.activity}
                        </p>
                      </div>

                    </div>

                    <div className="leaderboard-score">
                      <p className="leaderboard-score-value">
                        {Math.round(
                          Number(activity.score ?? 0)
                        )}
                      </p>

                      <p className="leaderboard-score-label">
                        Score
                      </p>
                    </div>

                  </div>
                )
              )}
            </div>

          </section>
        )}

        {sortedActivities.length === 0 && (
          <div className="leaderboard-empty">
            No progress activities yet.
          </div>
        )}

      </div>
    </div>
  );
}
